import { Dispatch, FC, lazy, SetStateAction, Suspense, useEffect } from "react";
import { Interactions } from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../store";

import { overviewParameters } from "./constants";
import { capitalizeAssociatedPress } from "../helpers/string/capitalize";
import { prepareCommonStats } from "./stats/helpers";
import Stats from "./stats/Stats";
import { Skeleton } from "../components/DataDisplay/Skeleton";
import { SubSectionWithStyles } from "../ui/layout";

const StatBarGraph = lazy(() => import("./stats/StatBarGraph"));

type SiteOverviewProps = {
  setIsSiteOverview: Dispatch<SetStateAction<boolean>>;
  service: keyof Interactions;
};

export const SiteOverview: FC<React.PropsWithChildren<SiteOverviewProps>> = ({
  setIsSiteOverview,
  service,
}) => {
  const companySiteOverview = useAppSelector(
    (state) => state.dashboard.companySiteOverview,
  );
  const { loading, error } = companySiteOverview;

  const targetReport = companySiteOverview.reports?.[service];

  const interactions = companySiteOverview.interactions;
  const stats = interactions?.[service]
    ? prepareCommonStats(interactions[service])
    : null;
  const isStats = !!stats?.some((stat) => stat.primaryStat !== 0);

  const histograms = targetReport?.histogram ?? [];
  const dataset = histograms.map((h) => h.summary.successful.total);
  const isHistogramData = dataset.some((d) => d !== 0);

  useEffect(() => {
    setIsSiteOverview(isHistogramData || isStats);
  }, [setIsSiteOverview, isHistogramData, isStats]);

  return isHistogramData || isStats ? (
    <SubSectionWithStyles
      title={capitalizeAssociatedPress(
        overviewParameters[service].sectionTitle,
      )}
      isSubHeader={false}
      withGroup={false}
    >
      {stats && isStats && (
        <Stats loading={loading} error={error} stats={stats} />
      )}

      {isHistogramData && (
        <Suspense fallback={<Skeleton />}>
          <StatBarGraph dataset={dataset} service={service} loading={loading} />
        </Suspense>
      )}
    </SubSectionWithStyles>
  ) : null;
};
